import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import AttemptDetailView, {
  testName,
  type AttemptListItem,
  type AttemptDetail,
  type QuestionRef,
} from '../attempts/AttemptDetailView';

const TYPE_OPTIONS = ['all', 'ubt', 'quiz', 'cbt', 'live'];

export default function UserActivityDrawer({
  open,
  onClose,
  userId,
  userLabel,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  userLabel: string;
}) {
  const [page, setPage] = useState(1);
  const [type, setType] = useState('all');
  const [passed, setPassed] = useState<'all' | 'true' | 'false'>('all');
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['user-attempts', userId, page, type, passed],
    enabled: open && !!userId,
    queryFn: async () => {
      const res = await api.get(`/users/${userId}/attempts`, {
        params: {
          page,
          limit: 10,
          ...(type !== 'all' ? { type } : {}),
          ...(passed !== 'all' ? { passed } : {}),
        },
      });
      return res.data;
    },
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['user-attempt-detail', userId, selectedAttemptId],
    enabled: open && !!selectedAttemptId,
    queryFn: async () => {
      const res = await api.get(`/users/${userId}/attempts/${selectedAttemptId}`);
      return res.data.data as { attempt: AttemptDetail; questions: QuestionRef[] };
    },
  });

  const attempts: AttemptListItem[] = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  const handleClose = () => {
    setSelectedAttemptId(null);
    setPage(1);
    setType('all');
    setPassed('all');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={selectedAttemptId ? 'Attempt Detail' : `Activity — ${userLabel}`}
      variant="drawer"
    >
      {selectedAttemptId ? (
        <AttemptDetailView
          loading={detailLoading}
          detail={detailData}
          onBack={() => setSelectedAttemptId(null)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              className="input-field w-40"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t === 'all' ? 'All types' : t.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={passed}
              onChange={(e) => {
                setPassed(e.target.value as 'all' | 'true' | 'false');
                setPage(1);
              }}
              className="input-field w-40"
            >
              <option value="all">All results</option>
              <option value="true">Passed</option>
              <option value="false">Failed</option>
            </select>
            <p className="text-sm text-gray-500 ml-auto">{pagination.total} attempts</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Test / Quiz</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Type</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Score</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Result</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : attempts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400">
                      No attempts found
                    </td>
                  </tr>
                ) : (
                  attempts.map((a) => {
                    const total = a.totalCorrect + a.totalWrong + a.totalSkipped;
                    return (
                      <tr key={a._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-900">{testName(a)}</td>
                        <td className="py-3 px-4">
                          <span className="badge bg-primary-50 text-primary-700">
                            {a.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {a.score}/{total}
                        </td>
                        <td className="py-3 px-4">
                          {a.passed === false ? (
                            <span className="badge bg-danger-50 text-danger-700">Failed</span>
                          ) : (
                            <span className="badge bg-success-50 text-success-700">Passed</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(a.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setSelectedAttemptId(a._id)}
                            className="text-xs text-primary-700 hover:underline"
                          >
                            View questions
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.totalPages}
                className="btn-secondary text-xs flex items-center gap-1"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
